import React, { useState } from "react";
import { COLORS, FIT_PLACEHOLDER, VERDICTS } from "../data/profile";
import { FIT_SYSTEM_PROMPT } from "../data/prompts";
import { assessFit } from "../api";
import { useLang, t } from "../LanguageContext";

function FitResult({ result, lang }) {
  const v = VERDICTS[result.verdict] || VERDICTS.worth_conversation;

  return (
    <div style={{ marginTop: 22, animation: "fadeIn 0.35s cubic-bezier(0.22, 1, 0.36, 1)" }}>
      <div
        style={{
          background: v.bg,
          border: `2px solid ${v.color}`,
          borderRadius: 12,
          padding: 18,
          marginBottom: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 20 }}>{v.icon}</span>
          <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(16px, 4vw, 18px)", color: v.color }}>
            {t(v.label, lang)}
          </span>
        </div>
        <p style={{ fontSize: 14, color: "#333", lineHeight: 1.6 }}>{result.headline}</p>
      </div>

      {result.match_reasons && result.match_reasons.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ fontSize: 11, color: "#2D5F2D", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
            {lang === "en" ? "Why it could work" : "Pourquoi ça pourrait fonctionner"}
          </h4>
          {result.match_reasons.map((r, i) => (
            <p key={i} style={{ fontSize: 13, color: "#444", margin: "4px 0", paddingLeft: 10, borderLeft: `2px solid ${COLORS.green}`, lineHeight: 1.6 }}>{r}</p>
          ))}
        </div>
      )}

      {result.gap_flags && result.gap_flags.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ fontSize: 11, color: "#8B2D2D", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
            {lang === "en" ? "Honest gaps" : "Lacunes honnêtes"}
          </h4>
          {result.gap_flags.map((g, i) => (
            <p key={i} style={{ fontSize: 13, color: "#666", margin: "4px 0", paddingLeft: 10, borderLeft: "2px solid #C88", lineHeight: 1.6 }}>{g}</p>
          ))}
        </div>
      )}

      <div style={{ background: COLORS.warm, borderRadius: 10, padding: 14, marginTop: 10 }}>
        <p style={{ fontSize: 13, color: "#333", lineHeight: 1.6 }}>{result.recommendation}</p>
      </div>
    </div>
  );
}

export default function FitTab() {
  const [jd, setJd] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { lang } = useLang();

  const runAssessment = async () => {
    if (!jd.trim() || loading) return;
    setLoading(true);
    setResult(null);
    const assessment = await assessFit(FIT_SYSTEM_PROMPT, jd.trim());
    setResult(assessment);
    setLoading(false);
  };

  return (
    <div>
      <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(20px, 5vw, 24px)", marginBottom: 6, color: COLORS.text }}>
        {lang === "en" ? "Fit Assessment" : "Évaluation d'Adéquation"}
      </h2>
      <p style={{ fontSize: 13, color: COLORS.muted, marginBottom: 18, lineHeight: 1.6 }}>
        {lang === "en"
          ? "Paste a role or project brief. The AI honestly assesses whether I am the right person, including when I am not."
          : "Collez une description de poste ou de projet. L'IA évalue honnêtement si je suis la bonne personne, y compris quand je ne le suis pas."}
      </p>

      <textarea
        value={jd}
        onChange={(e) => setJd(e.target.value)}
        placeholder={t(FIT_PLACEHOLDER, lang)}
        style={{
          width: "100%",
          minHeight: 140,
          maxHeight: 300,
          padding: 14,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          fontSize: 16,
          resize: "vertical",
          lineHeight: 1.6,
          background: "#fff",
          transition: "border-color 0.2s",
        }}
      />

      <button
        onClick={runAssessment}
        disabled={loading || !jd.trim()}
        style={{
          marginTop: 12,
          padding: "12px 24px",
          background: loading || !jd.trim() ? "#CCC" : COLORS.dark,
          color: "#F5F0EB",
          border: "none",
          borderRadius: 8,
          cursor: loading ? "wait" : "pointer",
          fontSize: 14,
          fontWeight: 500,
          minHeight: 44,
          width: "100%",
          transition: "background 0.2s, transform 0.1s",
        }}
      >
        {loading ? (lang === "en" ? "Analyzing..." : "Analyse en cours...") : (lang === "en" ? "Assess Fit" : "Évaluer l'Adéquation")}
      </button>

      {loading && (
        <p style={{ textAlign: "center", padding: 30, fontSize: 13, color: COLORS.muted, animation: "pulse 1.5s infinite" }}>
          {lang === "en" ? "Analyzing against actual background..." : "Analyse en cours basée sur le parcours réel..."}
        </p>
      )}

      {result && !loading && <FitResult result={result} lang={lang} />}
    </div>
  );
}
